import { Box, Card, TextField, ToggleButton, ToggleButtonGroup, Button, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { grey } from '@mui/material/colors';
import { useState } from 'react';
import { useSnackbar } from '../../components/context/SnackbarContext';
import CanvasApi from '../../services/CanvasApi';

const EraseBG = ({
  onAction,
  canvasApi,
  id,
  setIsGenerating,
  // setCurrentImageId,
  imageCategories,
  setImageCategories,
  canvasWidth
}: {
  onAction: (newImage: { id: string; url: string }) => void;
  canvasApi: CanvasApi;
  id: string;
  setCurrentImageId?: (val: string) => void;
  setIsGenerating: (val: boolean) => void;
  imageCategories: string[];
  setImageCategories: (categories: string[]) => void;
  canvasWidth?: string;
}) => {
  console.log({
    onAction,
    canvasApi,
    id,
    setIsGenerating
  });

  const [tab, setTab] = useState<'prompt' | 'image'>('prompt');
  const [maskCreated, setMaskCreated] = useState(false);
  const [autoMaskCreated, setAutoMaskCreated] = useState(false);
  const [maskPrompt, setMaskPrompt] = useState('');
  const [maskImageUrl, setMaskImageUrl] = useState<string | null>(null);
  const [autoMaskImages, setAutoMaskImages] = useState<string[]>([]);
  const [selectedMaskUrl, setSelectedMaskUrl] = useState<string | null>(null);

  const { showSnackbar } = useSnackbar();

  const createMaskUsingPrompt = async () => {
    if (!maskPrompt) {
      showSnackbar('Please enter a prompt to create a mask.', 'error');
      return;
    }

    try {
      setIsGenerating(true);
      await canvasApi.executeImageGeneration(
        async request => ({
          ...request,
          subject: id,
          prompt: maskPrompt,
          operation: 'generate',
          imageCategory: 'mask'
        }),
        async (client, request) => {
          const { data } = await client.createMaskFromPrompt(request);
          const newMaskUrl = data?.images?.[0]?.url;
          if (newMaskUrl) {
            setMaskImageUrl(newMaskUrl);
            setSelectedMaskUrl(newMaskUrl);
            setMaskCreated(true);
          } else {
            showSnackbar('Could not retrieve a mask image from the response.', 'error');
          }
          return data;
        }
      );
    } catch (error) {
      const errorMessage = (error as any)?.response?.data?.message || (error as any)?.message || 'Something went wrong while creating the mask.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const createAutoMasks = async () => {
    try {
      setIsGenerating(true);

      await canvasApi.executeImageGeneration(
        async request => ({
          ...request,
          subject: id,
          prompt: '',
          imageCategory: 'mask'
        }),
        async (client, request) => {
          const { data } = await client.createAutoMasks(request);
          const newMasks = data?.individualMasks?.map((mask: any) => mask.url) || [];

          if (newMasks.length > 0) {
            setAutoMaskImages(newMasks);
            setAutoMaskCreated(true);
          } else {
            showSnackbar('Could not retrieve auto-generated masks from the response.', 'error');
          }

          return data;
        }
      );
    } catch (error) {
      const errorMessage = (error as any)?.response?.data?.message || (error as any)?.message || 'Something went wrong while creating the masks.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleErase = async () => {
    if (!selectedMaskUrl) {
      showSnackbar('Please select a mask to erase.', 'error');
      return;
    }

    try {
      setIsGenerating(true);
      await canvasApi.executeImageGeneration(
        async request => ({
          ...request,
          subject: id,
          mask: selectedMaskUrl,
          prompt: '',
          imageCategories: imageCategories
        }),
        async (client, request) => {
          const { data } = await client.eraseObjects(request);

          const newImage = data?.images?.[0];

          console.log('Erase successful!', data);
          // setCurrentImageId(data?.images?.[0]?.id);

          if (newImage.url && newImage.id) {
            onAction(newImage);
            if (newImage.imageCategories) {
              setImageCategories(newImage.imageCategories);
            }
          } else {
            showSnackbar('Could not retrieve new image from the erase response.', 'error');
          }

          return data;
        }
      );
    } catch (error) {
      const errorMessage = (error as any)?.response?.data?.message || (error as any)?.message || 'Something went wrong while erasing the object.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card
      sx={{
        backgroundColor: '#18181B',
        p: 3,
        width: canvasWidth==="true" ? '286px' :'320px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRadius: 2,
        height: 'max-content',
        maxHeight: '61vh',
        mt: '43px',
        mx: 'auto',
        overflowY: 'scroll'
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography color="#E5E5E5" fontFamily={'Inter'} fontSize="16px" fontWeight={600}>
          Erase Objects
        </Typography>

        <Tooltip
          title={
            <Typography fontSize="10px" color="black" width="200px" fontFamily={'Inter'}>
              Create a mask to specify the objects to erase then select the appropriate mask and click the Erase button.{' '}
            </Typography>
          }
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'white',
                color: 'black',
                borderRadius: 1,
                boxShadow: 1,
                p: 0.5
              }
            }
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ color: '#FAFAFA', cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography color="#E5E5E5" fontSize={14} fontFamily={'Inter'} fontWeight={400} lineHeight="100%">
          Create Mask
        </Typography>

        <Tooltip
          title={
            <Typography fontSize="10px" color="black" width="200px" fontFamily={'Inter'}>
              Select Use Prompt to create a mask for a specific object using a prompt or select Auto Generate to create a mask for each object automatically.
            </Typography>
          }
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'white',
                color: 'black',
                borderRadius: 1,
                boxShadow: 1,
                p: 0.5
              }
            }
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ color: '#FAFAFA', cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      <ToggleButtonGroup
        value={tab}
        exclusive
        onChange={(_, val) => val && setTab(val)}
        fullWidth
        sx={{
          backgroundColor: grey[800],
          borderRadius: 2,
          p: '2px',
          gap: '3px',
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            flex: 1,
            fontSize: '10px',
            color: 'rgba(168, 168, 168, 1)',
            border: 'none',
            borderRadius: '8px',
            padding: '2px',
            backgroundColor: 'transparent',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            gap: '10px',

            '&.Mui-selected': {
              color: 'rgba(168, 168, 168, 1)',
              background: 'linear-gradient(black, black) padding-box, linear-gradient(163deg, #0CAECF -29.1%, #580F6B 96.45%) border-box',
              border: '1px solid transparent',
              borderRadius: '8px'
            },

            '&:hover': {
              backgroundColor: grey[700]
            }
          },
          '& .MuiToggleButton-root[value="image"]': {
            fontSize: '12px',
            fontWeight: '400'
          },
          '& .MuiToggleButton-root[value="prompt"]': {
            fontSize: '12px',
            fontWeight: '500'
          }
        }}
      >
        <ToggleButton value="prompt">Use Prompt</ToggleButton>
        <ToggleButton value="image">Auto Generate</ToggleButton>
      </ToggleButtonGroup>

      {tab === 'prompt' ? (
        <>
          <Typography fontSize={12} fontFamily={'Inter'} color="#E5E5E5">
            Tip: Provide textual description of the object to mask then click Create Mask button.{' '}
          </Typography>
          <TextField
            placeholder="Specify an object e.g. football"
            multiline
            minRows={2}
            fullWidth
            variant="outlined"
            value={maskPrompt}
            onChange={e => setMaskPrompt(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                borderRadius: 2,
                fontSize: '12px',
                backgroundColor: '#18181B',
                height: '78px',
                '& fieldset': {
                  borderColor: '#333'
                },
                '&:hover fieldset': {
                  borderColor: '#555'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#333'
                },

                '& .MuiInputBase-inputMultiline': {
                  paddingBottom: '25px'
                }
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={createMaskUsingPrompt}
            sx={{
              width: 'max-content',
              borderRadius: '100px',
              fontSize: '14px',
              color: '#C0C0C0',
              padding: '2px 16px',
              borderColor: '#C0C0C0',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'gray',
                boxShadow: 'none'
              }
            }}
          >
            Create Mask
          </Button>
          {maskCreated && maskImageUrl && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2
              }}
            >
              <Box
                component="img"
                src={maskImageUrl}
                alt="Generated Mask"
                sx={{
                  width: '144px',
                  height: '96px',
                  borderRadius: '13px',
                  backgroundColor: '#18181B',
                  objectFit: 'cover'
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          <Typography fontSize={12} fontFamily={'Inter'} color="#E5E5E5">
            Tip: Click the Create Masks button to create a mask for each object in the image automatically.
          </Typography>
          <Button
            variant="outlined"
            onClick={createAutoMasks}
            sx={{
              width: 'max-content',
              borderRadius: '20px',
              color: '#c0c0c0',
              borderColor: '#C0C0C0',
              padding: '2px 16px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'gray',
                boxShadow: 'none'
              }
            }}
          >
            Create Masks
          </Button>
          {autoMaskCreated && autoMaskImages.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {autoMaskImages.map((imageUrl, index) => {
                const isSelected = imageUrl === selectedMaskUrl;
                return (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '96px',
                      borderRadius: '13px',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s ease-in-out'
                    }}
                    onClick={() => setSelectedMaskUrl(imageUrl)}
                  >
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={`Generated Mask ${index}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '13px',
                        objectFit: 'cover'
                      }}
                    />
                    {/* The Radio Button */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: `1px solid ${isSelected ? 'rgba(222, 233, 235, 1)' : '#A8A8A8'}`,
                        backgroundColor: isSelected ? 'rgba(0, 0, 0, 1)' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {/* The inner white circle when selected */}
                      {isSelected && (
                        <Box
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'white'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}

      <Button
        onClick={handleErase}
        sx={{
          maxWidth: '200px',
          maxHeight: '52px',
          fontSize: '14px',
          padding: '8px 30px',
          width: '75px',
          height: '29px',
          borderRadius: '170px',
          background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
          color: '#FFFFFF',
          textTransform: 'none',
          marginLeft: 'auto',
          fontWeight: '500'
        }}
      >
        Erase
      </Button>
    </Card>
  );
};

export default EraseBG;
